// utils/rbacHelper.js

export const ROLES = {
  SUPER_ADMIN: 'admin',
  MANAGER: 'manager',
  HR: 'hr',
  FIELD_EMPLOYEE: 'field_emp',
  OFFICE_EMPLOYEE: 'office_emp',
  SUB_ADMIN: 'sub_admin',
  DEALER: 'group_admin',
  USER: 'user',
  CLIENT: 'client',
  VIEWER: 'viewer'
};

export const normalizeRole = (role) => {
  if (!role) return "";
  const r = String(role).trim();
  const lower = r.toLowerCase();
  if (lower === "superadmin" || lower === "super_admin") return ROLES.SUPER_ADMIN;
  if (lower === "groupadmin" || lower === "group_admin" || lower === "dealer") return ROLES.DEALER;
  if (lower === "subadmin" || lower === "sub_admin") return ROLES.SUB_ADMIN;
  if (lower === "manager") return ROLES.MANAGER;
  if (lower === "hr") return ROLES.HR;
  if (lower === "office_emp" || lower === "officeemployee") return ROLES.OFFICE_EMPLOYEE;
  if (lower === "field_emp" || lower === "fieldemployee") return ROLES.FIELD_EMPLOYEE;
  if (lower === "client") return ROLES.CLIENT;
  if (lower === "viewer") return ROLES.VIEWER;
  if (lower === "user") return ROLES.USER;
  if (lower === "admin") return ROLES.SUPER_ADMIN;
  return lower;
};


// ✅ Role Hierarchy Definition (Top to Bottom)
export const ROLE_HIERARCHY = {
  [ROLES.SUPER_ADMIN]: 0,      // Highest
  [ROLES.MANAGER]: 1,
  [ROLES.HR]: 2,
  [ROLES.OFFICE_EMPLOYEE]: 3,
  [ROLES.FIELD_EMPLOYEE]: 4,
  [ROLES.SUB_ADMIN]: 5,
  [ROLES.DEALER]: 6,
  [ROLES.CLIENT]: 7,
  [ROLES.VIEWER]: 8,           // Lowest
  [ROLES.USER]: 9              // Legacy
};

// ✅ Define which roles can be created by each role
export const CREATABLE_ROLES = {
  [ROLES.SUPER_ADMIN]: [
    ROLES.MANAGER, 
    ROLES.HR, 
    ROLES.OFFICE_EMPLOYEE, 
    ROLES.FIELD_EMPLOYEE, 
    ROLES.SUB_ADMIN, 
    ROLES.DEALER, 
    ROLES.CLIENT, 
    ROLES.VIEWER,
    ROLES.USER
  ],
  [ROLES.MANAGER]: [
    ROLES.OFFICE_EMPLOYEE, 
    ROLES.FIELD_EMPLOYEE, 
    ROLES.SUB_ADMIN, 
    ROLES.DEALER,
    ROLES.CLIENT,
    ROLES.VIEWER,
    ROLES.USER
  ],
  [ROLES.OFFICE_EMPLOYEE]: [
    ROLES.FIELD_EMPLOYEE,
    ROLES.SUB_ADMIN,
    ROLES.DEALER,
    ROLES.CLIENT,
    ROLES.USER,
    ROLES.VIEWER
  ],
 [ROLES.SUB_ADMIN]: [
  ROLES.DEALER,
  ROLES.USER,      // ✅ ADD THIS
  ROLES.CLIENT,
  ROLES.VIEWER
],
[ROLES.DEALER]: [
  ROLES.USER,      // ✅ ADD THIS
  ROLES.CLIENT,
  ROLES.VIEWER
],
  [ROLES.CLIENT]: [
    ROLES.VIEWER
  ],
  [ROLES.USER]: [
    ROLES.VIEWER
  ],
  
  // Others cannot create users
  [ROLES.HR]: [
    ROLES.OFFICE_EMPLOYEE,
    ROLES.FIELD_EMPLOYEE,
    ROLES.SUB_ADMIN,
    ROLES.DEALER,
    ROLES.CLIENT,
    ROLES.USER,
    ROLES.VIEWER
  ],
  [ROLES.FIELD_EMPLOYEE]: [],
  [ROLES.VIEWER]: [],
  [ROLES.USER]: [
    ROLES.VIEWER
  ]
};

// ✅ Define manageable (viewable) roles for each role
export const MANAGEABLE_ROLES = {
  [ROLES.SUPER_ADMIN]: [
    ROLES.MANAGER,
    ROLES.HR,
    ROLES.OFFICE_EMPLOYEE,
    ROLES.FIELD_EMPLOYEE,
    ROLES.SUB_ADMIN,
    ROLES.DEALER,
    ROLES.CLIENT,
    ROLES.VIEWER,
    ROLES.USER
  ],
  [ROLES.MANAGER]: [
    ROLES.OFFICE_EMPLOYEE,
    ROLES.FIELD_EMPLOYEE,
    ROLES.SUB_ADMIN,
    ROLES.DEALER,
    ROLES.CLIENT,
    ROLES.VIEWER,
    ROLES.USER
  ],
  [ROLES.HR]: [
    ROLES.OFFICE_EMPLOYEE,
    ROLES.FIELD_EMPLOYEE,
    ROLES.SUB_ADMIN,
    ROLES.DEALER,
    ROLES.CLIENT,
    ROLES.USER,
    ROLES.VIEWER
  ],
  [ROLES.OFFICE_EMPLOYEE]: [
    ROLES.FIELD_EMPLOYEE,
    ROLES.SUB_ADMIN,
    ROLES.DEALER,
    ROLES.CLIENT,
    ROLES.USER,
    ROLES.VIEWER,
  ],
[ROLES.SUB_ADMIN]: [
  ROLES.DEALER,
  ROLES.USER,      // ✅ ADD THIS
  ROLES.CLIENT,
  ROLES.VIEWER
],
[ROLES.DEALER]: [
  ROLES.USER,      // ✅ ADD THIS
  ROLES.CLIENT,
  ROLES.VIEWER
],
  [ROLES.CLIENT]: [
    ROLES.VIEWER
  ],
  [ROLES.VIEWER]: [],
  [ROLES.USER]: [
    ROLES.VIEWER
  ]
};

// Define permissions for each role
const ROLE_PERMISSIONS = {
  [ROLES.SUPER_ADMIN]: {
    // Dashboard & Views
    viewDashboard: true,
    viewUsers: true,
    viewDevices: true,
    viewAssets: true,
    viewEvents: true,
    viewMap: true,
    manageSensors: true,
    purchaseLicense: true,
    
    // User Management
    createUser: true,
    editUser: true,
    deleteUser: true,
    restoreUser: true,
    resetPassword: true,
    assignUserToGA: true,
    removeManagedUser: true,
    loginAsUser: true,
    
    // Device Management
    createDevice: true,
    editDevice: true,
    deleteDevice: true,
    assignDevice: true,
    sendCommands: true,
    manageGeofences: true,
    
    // Asset Management
    createAsset: true,
    editAsset: true,
    deleteAsset: true,
    restoreAsset: true,
    bulkUpload: true,
    
    // ✅ Credit Management
    viewCredits: true,
    allocateCredits: true,      // Give credits to Manager
    transferCredits: true,       // Transfer between any users
    viewCreditHistory: true,
    manageCredits: true,
    
    // ✅ Task Management
    createTask: true,
    assignTask: true,
    viewAllTasks: true,
    updateTask: true,
    deleteTask: true,
    
    // Other Features
    messageManagement: true,
    viewProtocols: true,
    
    quickActions: {
      addDevice: true,
      createUser: true,
      addAssets: true,
      protocols: true,
      viewAlerts: true,
      transferCredits: true,
      messageManagement: true
    }
  },
  
  [ROLES.MANAGER]: {
    viewDashboard: true,
    viewUsers: true,
    viewDevices: true,
    viewAssets: true,
    viewEvents: true,
    viewMap: true,
    
    createUser: true,
    editUser: true,
    deleteUser: true,
    restoreUser: true,
    resetPassword: true,
    assignUserToGA: true,
    removeManagedUser: true,
    loginAsUser: true,
    
    createDevice: true,
    editDevice: true,
    deleteDevice: true,
    assignDevice: true,
    sendCommands: true,
    manageGeofences: true,
    manageSensors: true,
    
    createAsset: true,
    editAsset: true,
    deleteAsset: true,
    restoreAsset: true,
    bulkUpload: true,
    
    // ✅ Credit Management - LIMITED
    viewCredits: true,
    allocateCredits: true,           // Can give to office_emp, sub_admin, dealer only
    transferCredits: true,           // ❌ Cannot transfer directly (only allocate from their pool)
    viewCreditHistory: true,          // Can see their allocation history
    manageCredits: false,             // ❌ Cannot modify credit system
    requestCredits: true,             // ✅ Can request more from Super Admin
    
    // ✅ Task Management
    createTask: true,
    assignTask: true,
    viewAllTasks: true,
    updateTask: true,
    deleteTask: true,
    
    messageManagement: true,
    viewProtocols: true,
    purchaseLicense: false,
    
    quickActions: {
      addDevice: true,
      createUser: true,
      addAssets: true,
      protocols: true,
      viewAlerts: true,
      transferCredits: false,
      messageManagement: true
    }
  },
  
  [ROLES.HR]: {
    viewDashboard: true,
    viewUsers: true,
    viewDevices: true,
    viewAssets: true,
    viewEvents: true,
    viewMap: true,
    
    createUser: true,
    editUser: true,
    deleteUser: true,
    restoreUser: true,
    resetPassword: true,
    assignUserToGA: true,
    removeManagedUser: true,
    loginAsUser: true,
    
    createDevice: true,
    editDevice: true,
    deleteDevice: true,
    assignDevice: true,
    sendCommands: true,
    manageGeofences: true,
    manageSensors: true,
    
    createAsset: true,
    editAsset: true,
    deleteAsset: true,
    restoreAsset: true,
    bulkUpload: true,
    
    // ✅ Credit Management - VIEW ONLY
    viewCredits: true,               // Can view all credit info
    allocateCredits: false,
    transferCredits: false,
    viewCreditHistory: true,
    manageCredits: false,
    requestCredits: false,
    
    // ✅ Task Management - VIEW ONLY
    createTask: true,
    assignTask: true,
    viewAllTasks: true,
    updateTask: true,
    deleteTask: true,
    
    messageManagement: true,
    viewProtocols: true,
    purchaseLicense: false,
    
    quickActions: {
      addDevice: true,
      createUser: true,
      addAssets: true,
      protocols: true,
      viewAlerts: true,
      transferCredits: false,
      messageManagement: true
    }
  },
  
  [ROLES.OFFICE_EMPLOYEE]: {
    viewDashboard: true,
    viewUsers: true,                 // ✅ Can view sub_admin, dealers, clients, viewers, field_emp
    viewDevices: true,
    viewAssets: true,
    viewEvents: true,
    viewMap: true,
    manageSensors: true,
    purchaseLicense: true,
    createDevice: true,
    
    createUser: true,
    editUser: true,
    deleteUser: true,
    restoreUser: true,
    resetPassword: true,
    assignUserToGA: true,
    removeManagedUser: true,
    loginAsUser: true,
    
    editDevice: true,
    deleteDevice: true,
    assignDevice: true,
    sendCommands: true,
    manageGeofences: true,
    
    createAsset: true,
    editAsset: true,
    deleteAsset: true,
    restoreAsset: true,
    bulkUpload: true,
    
    // ✅ Credit Management - NO MANAGE ACCESS
    viewCredits: true,
    allocateCredits: false,
    transferCredits: false,
    viewCreditHistory: true,
    manageCredits: false,
    requestCredits: false,
    
    // ✅ Task Management - ASSIGN TASKS
    createTask: true,                // ✅ Can create tasks
    assignTask: true,                // ✅ Can assign to field_emp
    viewAllTasks: true,              // Can view tasks they created
    updateTask: true,
    deleteTask: true,
    
    messageManagement: true,
    viewProtocols: true,
    
    quickActions: {
      addDevice: true,
      createUser: true,
      addAssets: true,
      protocols: true,
      viewAlerts: true,
      transferCredits: false,
      messageManagement: true
    }
  },
  
  [ROLES.FIELD_EMPLOYEE]: {
    viewDashboard: true,
    viewUsers: false,
    viewDevices: true,
    viewAssets: true,
    viewEvents: true,
    viewMap: true,
    
    createUser: false,
    editUser: false,
    deleteUser: false,
    restoreUser: false,
    resetPassword: false,
    assignUserToGA: false,
    removeManagedUser: false,
    loginAsUser: false,
    
    createDevice: true,
    editDevice: false,
    deleteDevice: false,
    assignDevice: false,
    sendCommands: false,
    manageGeofences: false,
    
    createAsset: false,
    editAsset: false,
    deleteAsset: false,
    restoreAsset: false,
    bulkUpload: false,
    
    // ✅ Credit Management - NO ACCESS
    viewCredits: false,
    allocateCredits: false,
    transferCredits: false,
    viewCreditHistory: false,
    manageCredits: false,
    requestCredits: false,
    
    // ✅ Task Management - UPDATE ONLY
    createTask: false,
    assignTask: false,
    viewAllTasks: false,             // ❌ Cannot view all tasks
    viewMyTasks: true,               // ✅ Can view assigned tasks only
    updateTask: true,                // ✅ Can update task status/progress
    deleteTask: false,
    
    messageManagement: false,
    viewProtocols: false,
    
    quickActions: {
      addDevice: false,
      createUser: false,
      addAssets: false,
      protocols: false,
      viewAlerts: true,
      transferCredits: false,
      messageManagement: false
    }
  },
  
  [ROLES.SUB_ADMIN]: {
    viewDashboard: true,
    viewUsers: true,                 // ✅ Can view dealers, clients, viewers
    viewDevices: true,
    viewAssets: true,
    viewEvents: true,
    viewMap: true,
    
    createUser: true,                // ✅ Can create dealers
    editUser: true,
    deleteUser: true,
    restoreUser: true,
    resetPassword: true,
    assignUserToGA: true,
    removeManagedUser: true,
    loginAsUser: true,
    
    createDevice: true,
    editDevice: true,
    deleteDevice: true,
    assignDevice: true,
    sendCommands: true,
    manageGeofences: true,
    manageSensors: true,
    purchaseLicense: true,
    
    createAsset: true,
    editAsset: true,
    deleteAsset: true,
    restoreAsset: true,
    bulkUpload: true,
    
    // ✅ Credit Management - DISTRIBUTE TO DEALERS
    viewCredits: true,
    allocateCredits: true,           // ✅ Can give tokens to dealers (from their allocated pool)
    transferCredits: false,
    viewCreditHistory: true,
    manageCredits: false,
    requestCredits: false,           // Cannot request (gets from office_emp)
    
    // ✅ Task Management - NO ACCESS
    createTask: false,
    assignTask: false,
    viewAllTasks: false,
    updateTask: false,
    deleteTask: false,
    
    messageManagement: true,
    viewProtocols: true,
    
    quickActions: {
      addDevice: true,
      createUser: true,
      addAssets: true,
      protocols: true,
      viewAlerts: true,
      transferCredits: false,
      messageManagement: true
    }
  },
  
  [ROLES.DEALER]: {
    viewDashboard: true,
    viewUsers: true,                 // ✅ Can view clients, viewers
    viewDevices: true,
    viewAssets: true,
    viewEvents: true,
    viewMap: true,
    manageGeofences: true,      // ✅ ADD THIS
    manageSensors: true,
    purchaseLicense: true,

    
    createUser: true,                // ✅ Can create clients
    editUser: true,
    deleteUser: true,                // ✅ Can delete clients
    restoreUser: false,
    resetPassword: true,
    assignUserToGA: false,
    removeManagedUser: false,
    loginAsUser: true,

    
    createDevice: true,
    editDevice: true,
    deleteDevice: false,
    assignDevice: true,
    sendCommands: true,
    
    createAsset: true,
    editAsset: true,
    deleteAsset: false,
    restoreAsset: false,
    bulkUpload: true,
    
    // ✅ Credit Management - DISTRIBUTE TO CLIENTS
    viewCredits: false,
    allocateCredits: false,           // ✅ Can give tokens to clients (from their allocated pool)
    transferCredits: false,
    viewCreditHistory: true,
    manageCredits: false,
    requestCredits: false,           // Cannot request (gets from sub_admin)
    
    // ✅ Task Management - NO ACCESS
    createTask: false,
    assignTask: false,
    viewAllTasks: false,
    updateTask: false,
    deleteTask: false,
    
    messageManagement: true,
    viewProtocols: true,
    
    quickActions: {
      addDevice: true,
      createUser: true,
      addAssets: true,
      protocols: true,
      viewAlerts: true,
      transferCredits: false,
      messageManagement: true
    }
  },
  
  [ROLES.CLIENT]: {
    viewDashboard: true,
    viewUsers: true,                 // ✅ Can view viewers
    viewDevices: true,
    viewAssets: false,
    viewEvents: true,
    viewMap: true,
    
    createUser: true,                // ✅ Can create viewers
    editUser: true,
    deleteUser: true,
    restoreUser: true,
    resetPassword: false,
    assignUserToGA: false,
    removeManagedUser: false,
    loginAsUser: false,
    
    createDevice: true,
    editDevice: true,
    deleteDevice: true,
    assignDevice: true,
    sendCommands: true,
    manageGeofences: true,
    purchaseLicense: false,
    
    createAsset: true,
    editAsset: true,
    deleteAsset: true,
    restoreAsset: true,
    bulkUpload: true,
    
    // ✅ Credit Management - NO ACCESS
    viewCredits: false,
    allocateCredits: false,
    transferCredits: false,
    viewCreditHistory: false,
    manageCredits: false,
    requestCredits: false,
    
    // ✅ Task Management - NO ACCESS
    createTask: false,
    assignTask: false,
    viewAllTasks: false,
    updateTask: false,
    deleteTask: false,
    
    messageManagement: false,
    viewProtocols: false,
    
    quickActions: {
      addDevice: true,
      createUser: true,              // Create viewer
      addAssets: true,
      protocols: false,
      viewAlerts: true,
      transferCredits: false,
      messageManagement: false
    }
  },
  
  [ROLES.VIEWER]: {
    viewDashboard: true,
    viewUsers: false,
    viewDevices: false,               // ✅ Only view map tracking
    viewAssets: false,
    viewEvents: false,
    viewMap: true,                   // ✅ Primary permission
    
    createUser: false,
    editUser: false,
    deleteUser: false,
    restoreUser: false,
    resetPassword: false,
    assignUserToGA: false,
    removeManagedUser: false,
    loginAsUser: false,
    
    createDevice: false,
    editDevice: false,
    deleteDevice: false,
    assignDevice: false,
    sendCommands: false,
    manageGeofences: false,
    manageSensors: false,
    purchaseLicense: false,
    
    createAsset: false,
    editAsset: false,
    deleteAsset: false,
    restoreAsset: false,
    bulkUpload: false,
    
    // ✅ Credit Management - NO ACCESS
    viewCredits: false,
    allocateCredits: false,
    transferCredits: false,
    viewCreditHistory: false,
    manageCredits: false,
    requestCredits: false,
    
    // ✅ Task Management - NO ACCESS
    createTask: false,
    assignTask: false,
    viewAllTasks: false,
    updateTask: false,
    deleteTask: false,
    
    messageManagement: false,
    viewProtocols: false,
    
    quickActions: {
      addDevice: false,
      createUser: false,
      addAssets: false,
      protocols: false,
      viewAlerts: false,
      transferCredits: false,
      messageManagement: false
    }
  },
  
  [ROLES.USER]: {
    // Legacy role - similar to client
    viewDashboard: true,
    viewUsers: true,
    viewDevices: true,
    viewAssets: false,
    viewEvents: true,
    viewMap: true,
    
    createUser: true,
    editUser: true,
    deleteUser: true,
    restoreUser: true,
    resetPassword: false,
    assignUserToGA: false,
    removeManagedUser: false,
    loginAsUser: false,
    
    createDevice: true,
    editDevice: true,
    deleteDevice: true,
    assignDevice: true,
    sendCommands: true,
    manageGeofences: true,
    manageSensors: false,
    purchaseLicense: false,
    
    createAsset: true,
    editAsset: true,
    deleteAsset: true,
    restoreAsset: true,
    bulkUpload: true,
    
    viewCredits: false,
    allocateCredits: false,
    transferCredits: false,
    viewCreditHistory: false,
    manageCredits: false,
    requestCredits: false,
    
    createTask: false,
    assignTask: false,
    viewAllTasks: false,
    updateTask: false,
    deleteTask: false,
    
    messageManagement: false,
    viewProtocols: false,
    
    quickActions: {
      addDevice: true,
      createUser: true,
      addAssets: true,
      protocols: false,
      viewAlerts: true,
      transferCredits: false,
      messageManagement: false
    }
  }
};

/**
 * Get permissions for a specific role
 * @param {string} role - User role
 * @returns {Object} - Permissions object
 */
export const getPermissions = (role) => {
  const normalizedRole = normalizeRole(role);
  return ROLE_PERMISSIONS[normalizedRole] || {
    viewDashboard: false,
    viewUsers: false,
    viewDevices: false,
    viewAssets: false,
    viewEvents: false,
    viewMap: false,
    createUser: false,
    editUser: false,
    deleteUser: false,
    restoreUser: false,
    resetPassword: false,
    assignUserToGA: false,
    removeManagedUser: false,
    loginAsUser: false,
    createDevice: false,
    editDevice: false,
    deleteDevice: false,
    assignDevice: false,
    sendCommands: false,
    manageGeofences: false,
    manageSensors: false,
    purchaseLicense: false,
    createAsset: false,
    editAsset: false,
    deleteAsset: false,
    restoreAsset: false,
    bulkUpload: false,
    viewCredits: false,
    allocateCredits: false,
    transferCredits: false,
    viewCreditHistory: false,
    manageCredits: false,
    requestCredits: false,
    createTask: false,
    assignTask: false,
    viewAllTasks: false,
    viewMyTasks: false,
    updateTask: false,
    deleteTask: false,
    messageManagement: false,
    viewProtocols: false,
    quickActions: {
      addDevice: false,
      createUser: false,
      addAssets: false,
      protocols: false,
      viewAlerts: false,
      transferCredits: false,
      messageManagement: false
    }
  };
};

/**
 * Check if user has specific permission
 * @param {string} role - User role
 * @param {string} permission - Permission to check
 * @returns {boolean}
 */
export const hasPermission = (role, permission) => {
  const permissions = getPermissions(role);
  return permissions[permission] === true;
};

/**
 * ✅ Check if user can create a specific role
 * @param {string} userRole - Current user's role
 * @param {string} targetRole - Role being created
 * @returns {boolean}
 */
export const canCreateRole = (userRole, targetRole) => {
  const actor = normalizeRole(userRole);
  const target = normalizeRole(targetRole);
  const creatableRoles = CREATABLE_ROLES[actor] || [];
  return creatableRoles.includes(target);
};

/**
 * ✅ Get filtered roles list based on user's permissions
 * @param {string} userRole - Current user's role
 * @returns {Array} - Filtered roles array
 */
export const getCreatableRoles = (userRole) => {
  return CREATABLE_ROLES[normalizeRole(userRole)] || [];
};

/**
 * ✅ Get roles that user can view/manage
 * @param {string} userRole - Current user's role
 * @returns {Array} - Manageable roles array
 */
export const getManageableRoles = (userRole) => {
  return MANAGEABLE_ROLES[normalizeRole(userRole)] || [];
};

/**
 * ✅ Check if userRole can manage targetRole
 * @param {string} userRole - Current user's role
 * @param {string} targetRole - Target user's role
 * @returns {boolean}
 */
export const canManageRole = (userRole, targetRole) => {
  const manageableRoles = MANAGEABLE_ROLES[normalizeRole(userRole)] || [];
  return manageableRoles.includes(normalizeRole(targetRole));
};

/**
 * ✅ Check if user can allocate credits to target role
 * @param {string} userRole - Current user's role
 * @param {string} targetRole - Target user's role
 * @returns {boolean}
 */
// utils/rbacHelper.js update

export const canAllocateCreditsTo = (userRole, targetRole) => {
  if (!hasPermission(userRole, 'allocateCredits')) return false;
  
  const allocatableRoles = {
    [ROLES.SUPER_ADMIN]: [ROLES.MANAGER, ROLES.SUB_ADMIN],
    [ROLES.MANAGER]: [ROLES.OFFICE_EMPLOYEE, ROLES.SUB_ADMIN, ROLES.DEALER],
    [ROLES.SUB_ADMIN]: [ROLES.DEALER, ROLES.CLIENT],
    [ROLES.DEALER]: [ROLES.CLIENT], // Dealer can only give to clients
    [ROLES.OFFICE_EMPLOYEE]: [ROLES.SUB_ADMIN]
  };
  
  const allowedTargets = allocatableRoles[userRole] || [];
  return allowedTargets.includes(targetRole);
};
/**
 * ✅ Check if user can assign tasks to target role
 * @param {string} userRole - Current user's role
 * @param {string} targetRole - Target user's role
 * @returns {boolean}
 */
export const canAssignTaskTo = (userRole, targetRole) => {
  if (!hasPermission(userRole, 'assignTask')) return false;
  
  // Only office_emp and manager can assign to field_emp
  if (targetRole === ROLES.FIELD_EMPLOYEE) {
    return [ROLES.SUPER_ADMIN, ROLES.MANAGER, ROLES.OFFICE_EMPLOYEE].includes(userRole);
  }
  
  return false;
};

/**
 * Get accessible tabs based on role
 * @param {string} role - User role
 * @returns {Array} - Array of accessible tab IDs
 */
export const getAccessibleTabs = (role) => {
  const permissions = getPermissions(role);
  const tabs = [];
  
  if (permissions.viewDashboard) tabs.push('statics');
  if (permissions.viewMap) tabs.push('map');
  if (permissions.viewUsers) tabs.push('users');
  if (permissions.viewDevices) tabs.push('devices');
  if (permissions.viewEvents) tabs.push('events');
  
  // ✅ Task Management Tabs
  if (permissions.createTask || permissions.assignTask || permissions.viewAllTasks) {
    tabs.push('task_management');
  }
  if (permissions.viewMyTasks) {
    tabs.push('my_tasks');
  }
  
  // Assignments tab for roles that can create/assign
  if (permissions.createDevice || permissions.assignUserToGA || permissions.viewAssets) {
    tabs.push('assignments');
  }
  
  return tabs;
};

/**
 * Filter user actions based on permissions
 * @param {string} role - User role
 * @returns {Object} - Available actions
 */
export const getUserActions = (role) => {
  const permissions = getPermissions(role);
  return {
    canEdit: permissions.editUser,
    canDelete: permissions.deleteUser,
    canRestore: permissions.restoreUser,
    canResetPassword: permissions.resetPassword,
    canRemoveManaged: permissions.removeManagedUser,
    canLoginAsUser: permissions.loginAsUser,
    canCreate: permissions.createUser
  };
};

/**
 * Filter device actions based on permissions
 * @param {string} role - User role
 * @returns {Object} - Available actions
 */
export const getDeviceActions = (role) => {
  const permissions = getPermissions(role);
  return {
    canEdit: permissions.editDevice,
    canDelete: permissions.deleteDevice,
    canManageGeofence: permissions.manageGeofences,
    canSendCommand: permissions.sendCommands,
    canCreate: permissions.createDevice,
    canManageSensors: permissions.manageSensors, // ✅ ADD

  };
};

/**
 * Filter asset actions based on permissions
 * @param {string} role - User role
 * @returns {Object} - Available actions
 */
export const getAssetActions = (role) => {
  const permissions = getPermissions(role);
  return {
    canEdit: permissions.editAsset,
    canDelete: permissions.deleteAsset,
    canRestore: permissions.restoreAsset,
    canBulkUpload: permissions.bulkUpload,
    canCreate: permissions.createAsset
  };
};

/**
 * ✅ Filter credit actions based on permissions
 * @param {string} role - User role
 * @returns {Object} - Available credit actions
 */
export const getCreditActions = (role) => {
  const permissions = getPermissions(role);
  return {
    canView: permissions.viewCredits,
    canAllocate: permissions.allocateCredits,
    canTransfer: permissions.transferCredits,
    canViewHistory: permissions.viewCreditHistory,
    canManage: permissions.manageCredits,
    canRequest: permissions.requestCredits
  };
};

/**
 * ✅ Filter task actions based on permissions
 * @param {string} role - User role
 * @returns {Object} - Available task actions
 */
export const getTaskActions = (role) => {
  const permissions = getPermissions(role);
  return {
    canCreate: permissions.createTask,
    canAssign: permissions.assignTask,
    canViewAll: permissions.viewAllTasks,
    canViewMy: permissions.viewMyTasks,
    canUpdate: permissions.updateTask,
    canDelete: permissions.deleteTask
  };
};

/**
 * ✅ Get role display name
 * @param {string} role - Role key
 * @returns {string} - Display name
 */
export const getRoleDisplayName = (role) => {
  const displayNames = {
    [ROLES.SUPER_ADMIN]: 'Super Admin',
    [ROLES.MANAGER]: 'Manager',
    [ROLES.HR]: 'HR',
    [ROLES.FIELD_EMPLOYEE]: 'Field Employee',
    [ROLES.OFFICE_EMPLOYEE]: 'Office Employee',
    [ROLES.SUB_ADMIN]: 'Admin',
    [ROLES.DEALER]: 'Dealer',
    [ROLES.USER]: 'User',
    [ROLES.CLIENT]: 'Client',
    [ROLES.VIEWER]: 'Viewer'
  };
  return displayNames[role] || role;
};





// utils/rbacHelper.js (ADD BELOW YOUR EXISTING EXPORTS)

export const APP_PERMISSION_KEYS = Object.freeze([
  "viewDashboard",
  "viewUsers",
  "viewDevices",
  "viewAssets",
  "viewEvents",
  "viewMap",
  "createUser",
  "editUser",
  "deleteUser",
  "restoreUser",
  "resetPassword",
  "assignUserToGA",
  "removeManagedUser",
  "loginAsUser",
  "createDevice",
  "editDevice",
  "deleteDevice",
  "assignDevice",
  "sendCommands",
  "manageGeofences",
  "manageSensors",
  "purchaseLicense",
  "createAsset",
  "editAsset",
  "deleteAsset",
  "restoreAsset",
  "bulkUpload",
  "viewCredits",
  "allocateCredits",
  "transferCredits",
  "viewCreditHistory",
  "manageCredits",
  "requestCredits",
  "createTask",
  "assignTask",
  "viewAllTasks",
  "viewMyTasks",
  "updateTask",
  "deleteTask",
  "messageManagement",
  "viewProtocols",
]);

// --- map backend CRUD => app permissions ---
const mapCrudToAppPermissions = (rolePerms, userCrud = {}) => {
  // userCrud: { devicePermissions: {create,read,update,delete,command}, userPermissions: {create,read,update,delete,assign} }

  const d = userCrud.devicePermissions || {};
  const u = userCrud.userPermissions || {};

  // IMPORTANT:
  // app-permission = role permission AND per-user CRUD permission (if exists)
  // if CRUD key missing => fallback true (means backend didn't restrict that part)
  const andIfDefined = (roleValue, crudValue) => {
    if (crudValue === undefined || crudValue === null) return !!roleValue;
    return !!roleValue && !!crudValue;
  };

  return {
    ...rolePerms,

    // views
    viewDevices: andIfDefined(rolePerms.viewDevices, d.read),
    viewUsers: andIfDefined(rolePerms.viewUsers, u.read),

    // device CRUD
    createDevice: andIfDefined(rolePerms.createDevice, d.create),
    editDevice: andIfDefined(rolePerms.editDevice, d.update),
    deleteDevice: andIfDefined(rolePerms.deleteDevice, d.delete),
    assignDevice: andIfDefined(rolePerms.assignDevice, d.update),
    sendCommands: andIfDefined(rolePerms.sendCommands, d.command),
    manageGeofences: andIfDefined(rolePerms.manageGeofences, d.update),
    manageSensors: andIfDefined(rolePerms.manageSensors, d.update),

    // user CRUD
    createUser: andIfDefined(rolePerms.createUser, u.create),
    editUser: andIfDefined(rolePerms.editUser, u.update),
    deleteUser: andIfDefined(rolePerms.deleteUser, u.delete),
    resetPassword: andIfDefined(rolePerms.resetPassword, u.update),
    assignUserToGA: andIfDefined(rolePerms.assignUserToGA, u.assign),
  };
};

const applyAppPermissionOverrides = (permissions, appOverrides = {}) => {
  const out = { ...permissions };
  Object.entries(appOverrides || {}).forEach(([key, value]) => {
    if (typeof value === "boolean" && key in out) {
      out[key] = value;
    }
  });
  return out;
};

/**
 * ✅ Effective permissions for logged in user
 * - role permissions + backend userPermissions/devicePermissions restriction
 */
export const getEffectivePermissions = (userOrRole) => {
  // Backward compatible:
  // if you pass role string -> old behavior
  if (!userOrRole) return getPermissions(undefined);

  if (typeof userOrRole === "string") {
    return getPermissions(userOrRole);
  }

  const role = userOrRole.role;
  const rolePerms = getPermissions(role);
  const appOverrides = userOrRole?.permissions?.app || {};
  const rolePermsWithOverrides = applyAppPermissionOverrides(rolePerms, appOverrides);

  return mapCrudToAppPermissions(rolePermsWithOverrides, {
    devicePermissions: userOrRole.devicePermissions || {},
    userPermissions: userOrRole.userPermissions || {},
  });
};

/**
 * ✅ check permission using effective perms
 */
export const hasUserPermission = (userOrRole, permissionKey) => {
  const perms = getEffectivePermissions(userOrRole);
  return perms?.[permissionKey] === true;
};

export const clampAppPermissionsByActor = (targetPermissions = {}, actorUserOrRole) => {
  const actorPermissions = getEffectivePermissions(actorUserOrRole);
  const nextPermissions = {};

  APP_PERMISSION_KEYS.forEach((key) => {
    nextPermissions[key] =
      targetPermissions?.[key] === true && actorPermissions?.[key] === true;
  });

  return nextPermissions;
};

/**
 * ✅ Use this everywhere instead of getUserActions(role)
 * It accepts (user object) OR (role string)
 */
export const getUserActionsEffective = (userOrRole) => {
  const p = getEffectivePermissions(userOrRole);
  return {
    canEdit: !!p.editUser,
    canDelete: !!p.deleteUser,
    canRestore: !!p.restoreUser,
    canResetPassword: !!p.resetPassword,
    canRemoveManaged: !!p.removeManagedUser,
    canLoginAsUser: !!p.loginAsUser,
    canCreate: !!p.createUser,
  };
};

/**
 * ✅ Use this everywhere instead of getDeviceActions(role)
 */
export const getDeviceActionsEffective = (userOrRole) => {
  const p = getEffectivePermissions(userOrRole);
  return {
    canEdit: !!p.editDevice,
    canDelete: !!p.deleteDevice,
    canManageGeofence: !!p.manageGeofences,
    canSendCommand: !!p.sendCommands,
    canCreate: !!p.createDevice,
    canManageSensors: !!p.manageSensors,
  };
};

/**
 * ✅ Creator can only GRANT permissions they have
 * (so child never gets extra permissions)
 */
export const clampChildCrudByCreator = ({ creatorUser, childDevicePermissions, childUserPermissions }) => {
  const creator = creatorUser || {};
  const creatorDevice = creator.devicePermissions || {};
  const creatorUserPerm = creator.userPermissions || {};

  const outDevice = {
    read: !!(childDevicePermissions?.read && creatorDevice.read),
    create: !!(childDevicePermissions?.create && creatorDevice.create),
    update: !!(childDevicePermissions?.update && creatorDevice.update),
    delete: !!(childDevicePermissions?.delete && creatorDevice.delete),
    command: !!(childDevicePermissions?.command && creatorDevice.command),
  };

  const outUser = {
    read: !!(childUserPermissions?.read && creatorUserPerm.read),
    create: !!(childUserPermissions?.create && creatorUserPerm.create),
    update: !!(childUserPermissions?.update && creatorUserPerm.update),
    delete: !!(childUserPermissions?.delete && creatorUserPerm.delete),
    assign: !!(childUserPermissions?.assign && creatorUserPerm.assign),
  };

  return { devicePermissions: outDevice, userPermissions: outUser };
};


/**
 * ✅ Check if role is higher in hierarchy
 * @param {string} role1 - First role
 * @param {string} role2 - Second role
 * @returns {boolean} - True if role1 is higher than role2
 */
export const isRoleHigher = (role1, role2) => {
  return ROLE_HIERARCHY[role1] < ROLE_HIERARCHY[role2];
};
